import { TVL_FROM_BOUND_KEY, TVL_TO_BOUND_KEY } from '@/constants/state'
import { useMixpanel } from '@/context/mixpanel'
import { type Settings, useSettingsContext } from '@/context/settings'
import { useDebounce } from '@/hooks/useDebounce'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import type { PopoverProps } from '@radix-ui/react-popover'
import { formatAmount } from '@vaultsfyi/common/utils'
import { useEffect, useState } from 'react'
import { AccordionItem } from '../common/accordion'
import { Popover, PopoverContent } from '../common/popover'
import { FilterAccordionContent, FilterAccordionTrigger, FilterSelectTrigger } from './filterSelect'
import { TvlRangeSelector } from './tvlRangeSelector'

interface TvlFilterProps {
  className?: string
}

export const TvlFilter = ({ className, modal }: TvlFilterProps & Pick<PopoverProps, 'modal'>) => {
  const { tvlRange: settingsTvlRange } = useSettingsContext()
  const { selectedTvlRange, updateSearchState } = useVaultsSearchState()
  const [sliderTvlRange, setSliderTvlRange] = useState(selectedTvlRange ?? settingsTvlRange)
  const { trackEvent } = useMixpanel()

  useEffect(() => {
    setSliderTvlRange(selectedTvlRange)
  }, [selectedTvlRange])

  useDebounce(
    () => {
      const entries: [string, number | undefined][] = []
      if (sliderTvlRange[0] !== selectedTvlRange[0])
        entries.push([TVL_FROM_BOUND_KEY, sliderTvlRange[0] === settingsTvlRange[0] ? undefined : sliderTvlRange[0]])
      if (sliderTvlRange[1] !== selectedTvlRange[1])
        entries.push([TVL_TO_BOUND_KEY, sliderTvlRange[1] === settingsTvlRange[1] ? undefined : sliderTvlRange[1]])
      entries.length > 0 && updateSearchState(...entries)

      if (sliderTvlRange[0] !== settingsTvlRange[0] || sliderTvlRange[1] !== settingsTvlRange[1]) {
        trackEvent('Filter used', { Filter: 'TVL range', Value: `${sliderTvlRange[0]} - ${sliderTvlRange[1]}` })
      }
    },
    [sliderTvlRange, updateSearchState],
    500
  )

  return (
    <Popover
      modal={modal}
      onOpenChange={() => {
        setSliderTvlRange(selectedTvlRange)
      }}
    >
      <FilterSelectTrigger className={className}>
        <div className="flex w-full max-w-full items-center gap-2 truncate">
          <TriggerText sliderTvlRange={sliderTvlRange} settingsTvlRange={settingsTvlRange} />
        </div>
      </FilterSelectTrigger>
      <PopoverContent className="flex w-52 flex-col gap-1 p-4">
        <span className="text-sm">TVL range, USD</span>
        <TvlRangeSelector initialValue={sliderTvlRange} onChange={setSliderTvlRange} />
      </PopoverContent>
    </Popover>
  )
}

export const TvlFilterAccordion = () => (
  <AccordionItem value="tvl">
    <FilterAccordionTrigger name="TVL" />
    <FilterAccordionContent>
      <TvlFilterContent />
    </FilterAccordionContent>
  </AccordionItem>
)

const TvlFilterContent = () => {
  const { tvlRange: settingsTvlRange } = useSettingsContext()
  const { selectedTvlRange, updateSearchState } = useVaultsSearchState()
  const [sliderTvlRange, setSliderTvlRange] = useState(selectedTvlRange ?? settingsTvlRange)
  const { trackEvent } = useMixpanel()

  useEffect(() => {
    setSliderTvlRange(selectedTvlRange)
  }, [selectedTvlRange])

  useDebounce(
    () => {
      const entries: [string, number | undefined][] = []
      if (sliderTvlRange[0] !== selectedTvlRange[0])
        entries.push([TVL_FROM_BOUND_KEY, sliderTvlRange[0] === settingsTvlRange[0] ? undefined : sliderTvlRange[0]])
      if (sliderTvlRange[1] !== selectedTvlRange[1])
        entries.push([TVL_TO_BOUND_KEY, sliderTvlRange[1] === settingsTvlRange[1] ? undefined : sliderTvlRange[1]])
      entries.length > 0 && updateSearchState(...entries)

      if (sliderTvlRange[0] !== settingsTvlRange[0] || sliderTvlRange[1] !== settingsTvlRange[1]) {
        trackEvent('Filter used', { Filter: 'TVL range', Value: `${sliderTvlRange[0]} - ${sliderTvlRange[1]}` })
      }
    },
    [sliderTvlRange, updateSearchState],
    500
  )

  return (
    <div className="flex flex-col gap-1 p-2">
      <span className="text-sm">TVL range, USD</span>
      <TvlRangeSelector initialValue={sliderTvlRange} onChange={setSliderTvlRange} />
    </div>
  )
}

interface TriggerTextProps {
  sliderTvlRange: [number, number]
  settingsTvlRange: Settings['tvlRange']
}

const TriggerText = ({ sliderTvlRange, settingsTvlRange }: TriggerTextProps) => {
  if (!sliderTvlRange || (sliderTvlRange[0] === settingsTvlRange[0] && sliderTvlRange[1] === settingsTvlRange[1])) {
    return <span>TVL</span>
  }

  const lowerBoundText = formatAmount(sliderTvlRange[0], { notation: 'compact' })
  const upperBoundText = formatAmount(sliderTvlRange[1], { notation: 'compact' })

  if (sliderTvlRange[0] !== settingsTvlRange[0] && sliderTvlRange[1] === settingsTvlRange[1]) {
    return (
      <span>
        TVL {'>'} {lowerBoundText}
      </span>
    )
  }

  if (sliderTvlRange[0] === settingsTvlRange[0] && sliderTvlRange[1] !== settingsTvlRange[1]) {
    return (
      <span>
        TVL {'<'} {upperBoundText}
      </span>
    )
  }

  return (
    <span>
      TVL: {lowerBoundText} – {upperBoundText}
    </span>
  )
}
